/**
 * Este script gestiona la lógica completa de una página de colección,
 * incluyendo la renderización de etiquetas de producto, scroll infinito
 * para cargar más artículos y filtros dinámicos por botones que se
 * actualizan sin recargar la página.
 */

window.getTextColorForBackground = function(hexColor) {
  if (!hexColor || hexColor.length < 7) return '#000000';
  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#000000' : '#FFFFFF';
};

window.initializeProductLabels = function(container) {
  if (!container) return;
  const labelContainers = container.querySelectorAll('.product-labels[data-labels]:not([data-processed="true"])');
  
  labelContainers.forEach(cont => {
    cont.dataset.processed = 'true';
    const labelsDataString = cont.dataset.labels;
    if (!labelsDataString) return;
    try {
      const labels = JSON.parse(labelsDataString);
      if (!Array.isArray(labels) || labels.length === 0) return;
      
      const fragment = document.createDocumentFragment();
      labels.forEach(label => {
        const labelElement = document.createElement('small');
        labelElement.className = 'product-label';
        labelElement.textContent = label.title;
        
        const backgroundColor = label.color;
        const textColor = window.getTextColorForBackground(backgroundColor);
        
        labelElement.style.backgroundColor = backgroundColor;
        labelElement.style.color = textColor;
        fragment.appendChild(labelElement);
      });

      cont.innerHTML = '';
      cont.appendChild(fragment);
    } catch (e) {
      console.error('Error al procesar las etiquetas del producto:', e, labelsDataString);
    }
  });
};

document.addEventListener('DOMContentLoaded', function () {
  const productGridContainer = document.getElementById('ProductGridContainer');
  const filtrosContainer = document.getElementById('filtros-dinamicos');

  if (productGridContainer) {
    window.initializeProductLabels(document.body);
    const infiniteScroll = initInfiniteScroll(productGridContainer);
    if (filtrosContainer) {
      initDynamicFilters(productGridContainer, filtrosContainer, infiniteScroll);
    }
  }
});

function initInfiniteScroll(productGridContainer) {
  let productGrid, selectors, nextPage, isLoading, noMoreProducts, intersectionObserver, sectionId, isRebuildingState;

  selectors = {
    productGrid: '#product-grid',
    gridItem: '.grid__item',
    pagination: '.pagination-wrapper',
    loader: '#infinite-scroll-loader'
  };

  async function loadAndAppendPage(pageToLoad) {
    if (isLoading) return { success: false, hasMore: false };
    isLoading = true;
    const loader = document.querySelector(selectors.loader);
    if (loader) loader.style.display = 'block';

    try {
      const fetchUrl = new URL(window.location.href);
      fetchUrl.searchParams.set('page', pageToLoad);
      fetchUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      
      const newProducts = doc.querySelectorAll(selectors.gridItem);
      const newPagination = doc.querySelector(selectors.pagination);

      if (newProducts.length > 0) {
        const fragment = document.createDocumentFragment();
        newProducts.forEach(product => fragment.appendChild(product));
        productGrid.appendChild(fragment);

        if (window.initializeProductLabels) {
          window.initializeProductLabels(productGrid);
        }

        if (!isRebuildingState) {
          const userUrl = new URL(window.location.href);
          userUrl.searchParams.set('page', pageToLoad);
          history.replaceState(null, '', userUrl.toString());
        }
        
        return { success: true, hasMore: newPagination !== null };
      } else {
        return { success: false, hasMore: false };
      }
    } catch (error) {
      console.error('Error al cargar más productos:', error);
      return { success: false, hasMore: false };
    } finally {
      isLoading = false;
      if (loader) loader.style.display = 'none';
    }
  }

  function setupIntersectionObserver() {
    if (intersectionObserver) intersectionObserver.disconnect();
    if (noMoreProducts) return;

    const triggerElement = productGrid.querySelector(`${selectors.gridItem}:last-child`);
    if (triggerElement) {
      intersectionObserver = new IntersectionObserver(async (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          intersectionObserver.disconnect();
          const result = await loadAndAppendPage(nextPage);
          if (result.success) {
            nextPage++;
            noMoreProducts = !result.hasMore;
            setupIntersectionObserver();
          } else {
            noMoreProducts = true;
          }
        }
      }, { rootMargin: '0px 0px 400px 0px' });
      
      intersectionObserver.observe(triggerElement);
    }
  }

  async function initialize() {
    productGrid = document.querySelector(selectors.productGrid);
    if (!productGrid) return;
    
    const sectionElement = productGrid.closest('[data-section-id]');
    if (!sectionElement) return;
    sectionId = sectionElement.dataset.sectionId;

    const paginationElement = document.querySelector(selectors.pagination);
    if (paginationElement) paginationElement.style.display = 'none';

    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page')) || 1;

    if (currentPage > 1) {
      isRebuildingState = true;
      productGrid.innerHTML = '';
      for (let i = 1; i <= currentPage; i++) {
        const result = await loadAndAppendPage(i);
        if (!result.success) {
          noMoreProducts = true;
          break;
        }
        noMoreProducts = !result.hasMore;
      }
      isRebuildingState = false;
    } else {
      noMoreProducts = !paginationElement;
    }
    
    nextPage = currentPage + 1;
    isLoading = false;
    setupIntersectionObserver();
  }
  
  function reset() {
    if (intersectionObserver) intersectionObserver.disconnect();
    productGrid = document.querySelector(selectors.productGrid);
    if (!productGrid) return;
    const paginationElement = productGridContainer.querySelector(selectors.pagination);
    if (paginationElement) paginationElement.style.display = 'none';
    nextPage = 2;
    isLoading = false;
    noMoreProducts = !paginationElement;
    setupIntersectionObserver();
  }

  initialize();
  return { reset };
}

function initDynamicFilters(productGridContainer, filtrosContainer, infiniteScroll) {
  const filtrosSecundariosStr = filtrosContainer.dataset.filtrosSecundarios;
  const collectionUrl = filtrosContainer.dataset.collectionUrl;
  const sectionId = productGridContainer.dataset.sectionId;
  const baseUrl = collectionUrl.endsWith('/') ? collectionUrl.slice(0, -1) : collectionUrl;
  
  const mapFiltros = {};
  if (filtrosSecundariosStr) {
    filtrosSecundariosStr.split(',').forEach(pair => {
      const parts = pair.split(':');
      if (parts.length >= 2) {
        const main = parts[0].trim();
        const subs = parts.slice(1).join(':').split('|').map(s => s.trim()).filter(Boolean);
        if (main) mapFiltros[main.toLowerCase().replace(/ /g, '-')] = subs;
      }
    });
  }

  let activeFilters = { level1: null, level2: null };

  const fetchCollectionData = () => {
    if (!sectionId) return;

    const tags = [activeFilters.level1, activeFilters.level2].filter(Boolean);
    const urlWithTags = tags.length > 0 ? `${baseUrl}/${tags.join('+')}` : baseUrl;
    const finalUrl = `${urlWithTags}?section_id=${sectionId}`;

    productGridContainer.classList.add('loading');

    fetch(finalUrl)
      .then(response => response.text())
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newGrid = html.getElementById('ProductGridContainer');

        if (newGrid) {
          productGridContainer.innerHTML = newGrid.innerHTML;
          
          if (window.initializeProductLabels) {
            window.initializeProductLabels(productGridContainer);
          }
          
          if (infiniteScroll) {
            infiniteScroll.reset();
          }
        }

        if (window.location.pathname !== urlWithTags) {
          history.pushState({ tags: activeFilters }, '', urlWithTags);
        }
      })
      .catch(e => console.error("Error al filtrar la colección:", e))
      .finally(() => productGridContainer.classList.remove('loading'));
  };

  const updateLevel2Filters = () => {
    const nivel2Container = document.getElementById('filtro-nivel-2');
    const nivel2Wrapper = document.getElementById('filtro-nivel-2-wrapper');
    if (!nivel2Container || !nivel2Wrapper) return;
    nivel2Container.innerHTML = '';
    const subfiltros = mapFiltros[activeFilters.level1] || [];
    if (activeFilters.level1 && subfiltros.length > 0) {
      nivel2Wrapper.style.display = 'block';
      subfiltros.forEach(sf => {
        const sfBtn = document.createElement('button');
        sfBtn.type = 'button';
        const filtroHandle = sf.toLowerCase().replace(/ /g, '-');
        sfBtn.className = 'filtro-boton';
        if (filtroHandle === activeFilters.level2) sfBtn.classList.add('activo');
        sfBtn.textContent = sf;
        sfBtn.dataset.filtro = filtroHandle;
        nivel2Container.appendChild(sfBtn);
      });
    } else {
      nivel2Wrapper.style.display = 'none';
    }
  };

  const syncUiFromState = () => {
    const nivel1Container = document.getElementById('filtro-nivel-1');
    if (nivel1Container) {
      nivel1Container.querySelectorAll('.filtro-boton').forEach(btn => {
        btn.classList.toggle('activo', btn.dataset.filtro === activeFilters.level1);
      });
    }
    updateLevel2Filters();
  };

  const syncStateFromUrl = () => {
    const currentPath = window.location.pathname;
    let tags = [];
    if (currentPath.startsWith(baseUrl + '/')) {
      tags = currentPath.substring(baseUrl.length + 1).split('+');
    }
    activeFilters.level1 = tags[0] || null;
    activeFilters.level2 = tags[1] || null;
    syncUiFromState();
  };
  
  filtrosContainer.addEventListener('click', function(event) {
    const button = event.target.closest('.filtro-boton');
    if (!button) return;
    const filtroSeleccionado = button.dataset.filtro;
    const parentId = button.parentElement.id;
    const isAlreadyActive = button.classList.contains('activo');
    if (parentId === 'filtro-nivel-1') {
      activeFilters.level1 = isAlreadyActive ? null : filtroSeleccionado;
      activeFilters.level2 = null;
    } else if (parentId === 'filtro-nivel-2') {
      activeFilters.level2 = isAlreadyActive ? null : filtroSeleccionado;
    }
    syncUiFromState();
    fetchCollectionData();
  });
  
  syncStateFromUrl();
  window.addEventListener('popstate', syncStateFromUrl);
}